/**
 * Shared, framework-free helper for embedded base64 image data URIs. No imports.
 *
 * Some question content stores a figure as a BARE `data:image/...;base64,...` string sitting
 * directly in the markdown (no `<img>` tag, no `![](...)` syntax) — e.g. deaveris-flow inlining a
 * rendered chart at the top of a stimulus. The on-screen renderer already tolerates this: the
 * client's extractDataUriImages (client/src/utils/contentRenderer.jsx) wraps such a bare URI in an
 * `<img>` before markdown conversion, so the student sees a picture. Any other consumer that turns
 * this content into HTML (notably the server-side PDF export in server/lib/questionsPdf.js) must do
 * the same, or the raw base64 renders as a literal-text blob.
 *
 * This module is the canonical home for that wrap so the client and the PDF export can't drift.
 * Imported by the server via a relative path and by the client via the `@shared` alias (same
 * mechanism as shared/math/detection.js).
 */

/**
 * Matches, in priority order:
 *   1. a fenced (```...```) or inline (`...`) code region, OR an existing `<img ...>` tag
 *      (capture group 1) — these are returned UNTOUCHED, so a data URI shown as code or already
 *      wrapped in an <img> is never rewritten (makes the wrap idempotent and code-safe);
 *   2. a bare `data:image/<subtype>;base64,<body>` URI. The base64 character class stops at the
 *      first character outside the alphabet (whitespace/newline), which is the boundary before any
 *      surrounding prose. The base64 alphabet contains no `>`, so wrapping is safe for a subsequent
 *      HTML tag scan.
 */
export const BARE_DATA_URI_RE =
  /(```[\s\S]*?```|`[^`]*`|<img\s[^>]*>)|data:image\/[-a-zA-Z0-9.+]+;base64,[A-Za-z0-9+/=]+/g

/**
 * Wrap every bare `data:image/...;base64,...` string in the text with an `<img>` tag. Code regions
 * and data URIs already inside an `<img>` are preserved verbatim, so the transform is idempotent.
 *
 * @param {string} text
 * @returns {string}
 */
export function wrapBareDataUriImages(text) {
  if (!text || typeof text !== 'string' || !text.includes('data:image')) {
    return text || ''
  }
  BARE_DATA_URI_RE.lastIndex = 0
  return text.replace(BARE_DATA_URI_RE, (match, protectedRegion) =>
    protectedRegion ? protectedRegion : `<img src="${match}" alt="" />`
  )
}

export default { BARE_DATA_URI_RE, wrapBareDataUriImages }
